import React, {ReactNode, useEffect, useState} from 'react';
import {classes, st} from './Layout.st.css';
import {MainLoader} from './MainLoader/MainLoader';
import {AsideLoader} from './AsideLoader/AsideLoader';
import {HeaderLoader} from './HeaderLoader/HeaderLoader';
import {useEnvironment, useExperiments} from '@wix/yoshi-flow-editor';
import {A11yIds, SPECS} from '../../constants';
import {useLocaleKeys} from '../../../locale-keys/LocaleKeys';

export interface LayoutProps {
  isLoading: boolean;
  showErrorState?: boolean;
  isOneColumnView?: boolean;
  Header: () => ReactNode;
  Aside: () => ReactNode;
  Footer: () => ReactNode;
  ErrorState: () => ReactNode;
  children: ReactNode;
  dataHook: string;
  dir: string;
}

export enum LayoutDataHooks {
  mainSection = 'LayoutDataHooks.mainSection',
  oneColumnViewSummary = 'LayoutDataHooks.oneColumnViewSummary',
  twoColumnViewSummary = 'LayoutDataHooks.twoColumnViewSummary',
}

/* eslint-disable sonarjs/cognitive-complexity */
export const Layout = ({
  dataHook,
  dir,
  isLoading,
  showErrorState,
  isOneColumnView,
  Header,
  Aside,
  Footer,
  ErrorState,
  children,
}: LayoutProps) => {
  const {experiments} = useExperiments();
  const localeKeys = useLocaleKeys();
  const [loaded, setLoaded] = useState(false);
  const [style, setStyle] = useState<{zoom?: number; height: string} | undefined>(undefined);

  const {isMobile} = useEnvironment();

  useEffect(() => {
    if (!experiments.enabled(SPECS.ShouldFixZoomForCheckoutInClassic)) {
      return;
    }
    const zoom = getZoomForMobileClassicEditorFix(isMobile);
    setStyle(zoom ? {zoom, height: `calc(100vh / ${zoom})`} : undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLoaded(!isLoading);
  }, [isLoading]);

  /* istanbul ignore next */
  return (
    <div
      data-hook={dataHook}
      className={st(classes.root, showErrorState ? classes.errorStateContainer : undefined)}
      dir={dir}
      style={style}>
      {loaded || showErrorState ? Header() : <HeaderLoader />}
      {showErrorState ? (
        ErrorState()
      ) : (
        <div className={classes.container}>
          {isOneColumnView && (
            <aside data-hook={LayoutDataHooks.oneColumnViewSummary} className={classes.aside}>
              {loaded ? Aside() : null}
            </aside>
          )}
          <section
            className={classes.main}
            data-hook={LayoutDataHooks.mainSection}
            aria-label={localeKeys.checkout.checkoutForm.ariaLabel()}>
            {loaded ? children : <MainLoader />}
          </section>

          {(!isOneColumnView || !loaded) && (
            <section
              aria-labelledby={A11yIds.SummarySection}
              data-hook={LayoutDataHooks.twoColumnViewSummary}
              className={classes.aside}>
              {loaded ? Aside() : <AsideLoader />}
            </section>
          )}
        </div>
      )}
      {loaded && Footer()}
    </div>
  );
};

function getZoomForMobileClassicEditorFix(isMobile: boolean) {
  const editorBrand = (window as {commonConfig?: {brand: string}})?.commonConfig?.brand;
  const isClassicEditor = editorBrand === 'wix';
  if (!isMobile || !isClassicEditor) {
    return;
  }
  const width = window?.innerWidth || 320;
  const actualWidth = window?.screen?.availWidth || width;
  return width / actualWidth;
}
